import { RESTARTABLE_REASONS } from '../constants';

// One transparent recovery attempt, then a human. When the cloud
// bounces back to /login with a RESTARTABLE reason (stale/superseded
// OIDC state, consumed code, transient verification miss), the login
// screen restarts the flow automatically ONCE — fresh state is minted
// server-side and `next` is preserved — so the parallel-window incident
// self-heals without the user reading an error. The guard records the
// attempt in sessionStorage (per-tab, survives the IdP round trip); a
// second consecutive failure inside the window renders the explicit
// "Start again" card instead, so a genuinely broken IdP can never spin
// a redirect loop.

const STORAGE_KEY = 'fred-auth-auto-restart-at';

// Wide enough to cover a slow IdP round trip; narrow enough that a
// user returning much later gets a fresh transparent attempt.
export const RESTART_GUARD_WINDOW_MS = 2 * 60_000;

function lastAutoRestartAt(): number | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * True when the given bounce reason should be recovered transparently
 * by restarting the OIDC flow now. False when the reason is not
 * restartable, when an auto-restart was already spent inside the guard
 * window, or when sessionStorage is unavailable (no storage = no way
 * to bound the loop = never auto-restart).
 */
export function shouldAutoRestart(reason: string, now: number = Date.now()): boolean {
  if (!RESTARTABLE_REASONS.has(reason)) return false;
  if (!storageAvailable()) return false;
  const last = lastAutoRestartAt();
  return last === null || now - last > RESTART_GUARD_WINDOW_MS;
}

/** Record that the one transparent attempt has been spent. */
export function recordAutoRestart(now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable: shouldAutoRestart already returns false in
    // that case, so there is no loop to bound.
  }
}

function storageAvailable(): boolean {
  try {
    const probeKey = `${STORAGE_KEY}-probe`;
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}
